import { useMemo, useState, useCallback, useEffect } from "react";
import Card from "./Card";
import Modal from "./Modal";
import Button from "./Buttons";
import { useApi } from "../hooks/useApi";
import { useShowEnterpriseCsvReport } from "../contexts/FeatureFlagsContext";
import {
    buildTrustServicesCategoryCsvRows,
    isExcludedCategory,
    isSoc2Framework,
    readinessWording,
    scopeHintLine,
    scopeWording,
    passingWording,
} from "../utils/trustServicesCategories";
import {
    buildApplicabilityLine,
    buildEvidenceLine,
    complianceWindowText,
    formatReadiness,
    futureStatusLabel,
    FUTURE_ONLY_EXPLANATION,
    FUTURE_ONLY_READINESS_TEXT,
    FUTURE_READINESS_EXPLANATION,
    FUTURE_READINESS_HEADING,
    futureEffectiveControlsOf,
    groupFutureEffectiveControls,
    isFutureReadinessOnly,
    isUnscorable,
    supersededByText,
} from "../utils/grcReadinessText";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from "recharts";
import {
    tierHeadlineText,
    componentCellText,
    characteristicSummaryText,
    suggestedNextText,
    confidenceText,
    coverageText,
    completenessText,
    determinedNoteText,
    gapsHeadingText,
    gapItemText,
    improvementGroupTexts,
    nextTierNoteText,
    tierCoverageRows,
    tierCoverageState,
    tierCoverageCaption,
    buildCsfTierStatusCsvRows,
    buildCsfTierMappingCsvRows,
} from "../utils/csfTierText";

// Fill colour of one tier bar by its coverage state; the "5 of 8" caption carries the value, so
// the bar reads the same in greyscale and to a screen reader (aria-label below).
const TIER_BAR_FILL_CLASS = {
    met: "bg-green-600",
    partial: "bg-yellow-500",
    possible: "bg-green-100 border border-dashed border-green-500",
    unmet: "bg-gray-200",
    not_measured: "bg-gray-200",
};

const TIER_BAR_CAPTION_CLASS = {
    met: "text-green-800 font-semibold",
    partial: "text-yellow-700 font-semibold",
    possible: "text-gray-600",
    unmet: "text-gray-500",
    not_measured: "text-gray-400",
};

// Four vertical bars for NIST CSF Tiers 1–4, each filled to the share of the eight characteristics
// that meet the tier: green when all do, amber when some do (a partially met Tier 2 used to show
// nothing at all), pale green for the share that might (bounded), grey for none; an opted-out
// Tier 4 is an empty bar captioned "Not measured". Same data and colours as the PDF.
const CsfTierBar = ({ tier }) => {
    const rows = tierCoverageRows(tier);
    const describe = rows.map(r => `Tier ${r.tier} ${r.name}: ${tierCoverageCaption(r)}`).join('; ');
    return (
        <div role="img" aria-label={`Implementation Tier: ${tierHeadlineText(tier)}. Tier coverage — ${describe}`}>
            <div className="text-[10px] text-gray-500 mb-1">Tier coverage — characteristics meeting each tier</div>
            <div className="grid grid-cols-4 gap-2">
                {rows.map((row) => {
                    const state = tierCoverageState(row);
                    const total = Math.max(row.total, 1);
                    const metPct = row.notMeasured ? 0 : Math.round((row.met / total) * 100);
                    const possiblePct = row.notMeasured ? 0 : Math.round((row.possible / total) * 100);
                    return (
                        <div key={row.tier}>
                            <div className="h-12 bg-gray-200 rounded-sm flex flex-col justify-end overflow-hidden" data-testid={`csf-tier-bar-${row.tier}`}>
                                {possiblePct > 0 && (
                                    <div className={TIER_BAR_FILL_CLASS.possible} style={{ height: `${possiblePct}%` }} />
                                )}
                                {metPct > 0 && (
                                    <div className={TIER_BAR_FILL_CLASS[state === 'met' ? 'met' : 'partial']} style={{ height: `${metPct}%` }} />
                                )}
                            </div>
                            <div className={`text-[10px] text-center mt-1 ${TIER_BAR_CAPTION_CLASS[state]}`}>
                                <div>Tier {row.tier} · {row.name}</div>
                                <div>{tierCoverageCaption(row)}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const getBarColor = (coverage) => {
    if (coverage >= 90) return "#166534"; // dark green
    if (coverage >= 70) return "#22c55e"; // green
    if (coverage >= 50) return "#facc15"; // yellow
    if (coverage >= 25) return "#f97316"; // orange
    return "#dc2626";                     // red
};

const INDUSTRY_AVG_COLOR = "#94a3b8";
const READINESS_LABEL = "Readiness Indicator";
const BENCHMARK_LABEL = "Peer Benchmark (est.)";

// What a framework calls its scored items, in the PDF's own words: the server sends them as
// itemNoun / itemNounPlural ("requirements" for NERC CIP and 800-171, "Subcategories" for CSF,
// "criteria" for SOC 2, "controls" for ISO and PISF), so the stat boxes, the tooltip, the evidence
// line and the CSV all match the PDF (AX-213). The fallback covers a payload without the field.
export const itemNounPluralOf = (row) =>
    row?.itemNounPlural || (isSoc2Framework(row?.framework) ? 'criteria' : 'controls');
export const itemNounFor = (row) => {
    const noun = itemNounPluralOf(row);
    // Each word, so HITRUST's "control references" reads "Control References" as the PDF tiles do.
    return noun.replace(/\b\w/g, c => c.toUpperCase());
};

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const d = payload[0].payload;
        return (
            <div className="bg-white border p-2 text-sm shadow-lg rounded">
                <div className="font-semibold">{d.frameworkDisplayName || d.framework}</div>
                <div>{READINESS_LABEL}: {formatReadiness(d)}</div>
                {d.industryAverageCoverage != null && (
                    <div className="text-slate-500">{BENCHMARK_LABEL}: {d.industryAverageCoverage}%</div>
                )}
                {d.dodAssessment && (
                    <div className="text-blue-700">Est. DoD-methodology score: {d.dodAssessment.score} / {d.dodAssessment.maxScore}</div>
                )}
                {d.csfTierAssessment && (
                    <div className="text-blue-700">Implementation Tier (Axaris-assessed): {tierHeadlineText(d.csfTierAssessment)}</div>
                )}
                <div>Passing {itemNounFor(d)}: {d.uniquePassingControls}</div>
                {d.uniquePartialControls > 0 && <div>Partial {itemNounFor(d)}: {d.uniquePartialControls}</div>}
                <div>Failing {itemNounFor(d)}: {d.uniqueFailingControls}</div>
                {d.notAssessedControls > 0 && <div className="text-slate-500">Not Assessed: {d.notAssessedControls}</div>}
            </div>
        );
    }
    return null;
};

const FUTURE_STATUS_CLASS = {
    Pass: "text-green-600",
    Partial: "text-amber-600",
    Fail: "text-red-600",
    "Not Assessed": "text-gray-500",
};

// Requirements approved but not yet enforceable, reported beside the readiness figure and never
// in it. The caller renders this only for a non-empty list, so every framework without
// future-effective controls keeps exactly the modal it had.
const FutureReadinessBlock = ({ controls }) => (
    <div className="border-t border-gray-100 pt-2 text-xs text-gray-500">
        <div className="text-sm font-semibold text-gray-700">{FUTURE_READINESS_HEADING}</div>
        <p className="mt-0.5">{FUTURE_READINESS_EXPLANATION}</p>
        {groupFutureEffectiveControls(controls).map(group => (
            <div key={group.standard} className="mt-2">
                <div className="font-medium text-gray-700">{group.standard}</div>
                {supersededByText(group.supersededBy) && (
                    <p className="text-gray-500">{supersededByText(group.supersededBy)}</p>
                )}
                <ul className="mt-1 space-y-0.5">
                    {group.controls.map(c => {
                        const status = futureStatusLabel(c.status);
                        const complianceWindow = complianceWindowText(c);
                        return (
                            <li key={c.controlId}>
                                <span className="font-medium text-gray-700">{c.controlId}</span>
                                {c.title ? <> — {c.title}</> : null}
                                {' · '}<span className={`font-semibold ${FUTURE_STATUS_CLASS[status]}`}>{status}</span>
                                {complianceWindow ? <> · {complianceWindow}</> : null}
                            </li>
                        );
                    })}
                </ul>
            </div>
        ))}
    </div>
);

// The expanded row of one gap characteristic: how many mapped indicators fall short of the tier
// they need. The questions behind them are not shown — no question id, question text or answer
// leaves the assessment view (AX-200) — so the counts are what the reader acts on.
const TierGapImprovements = ({ characteristic }) => {
    const groups = improvementGroupTexts(characteristic);
    if (groups.length === 0) {
        return <div className="mt-1 ml-4 text-gray-500">No mapped indicators to improve for this characteristic.</div>;
    }
    return (
        <ul className="mt-1 ml-4 list-disc space-y-0.5 text-gray-600">
            {groups.map(g => (
                <li key={g.key}>
                    {g.text}
                    {g.required && (
                        <span className="ml-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">required</span>
                    )}
                </li>
            ))}
        </ul>
    );
};

const escapeCsvField = (field) => {
    const str = String(field ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};


// ISO 27001:2022 only: the management-system clauses (4–10) the export lists. The questions and
// answers behind them are never exported — no report states them (AX-200) — so only the clause
// references are read out. `clauses` is the server's ordered list; the fallback derives the same
// references from `answers` so a payload from before that field existed still exports no question.
const clauseSortKey = (clause) =>
    (String(clause).match(/\d+/g) || []).map(n => n.padStart(4, '0')).join('.');

export const clauseReferencesFrom = (section) => {
    if (Array.isArray(section?.clauses)) return section.clauses;
    const seen = [];
    for (const answer of (section?.answers || [])) {
        for (const clause of (answer?.clauses || [])) {
            if (!seen.some(s => s.toLowerCase() === String(clause).toLowerCase())) seen.push(clause);
        }
    }
    return seen.sort((a, b) => clauseSortKey(a).localeCompare(clauseSortKey(b)));
};

// The Evidence Credit cell of one Control Details row. An unscored row contributes nothing to any
// percentage, so it has no credit to report: the server sends a plain 0 for it (the field is not
// nullable), which printed as "0%" beside "Not in scope" — a figure where the PDF prints none.
// Blank, not zero: zero is a score, and this row has none.
export const controlCreditCell = (control) =>
    control?.isScored === false || control?.evidenceCredit == null
        ? ''
        : Math.round(control.evidenceCredit * 100) + '%';

const GrcComplianceEstimate = ({ complianceData, assessmentId }) => {
    const apiCall = useApi();
    const showCsvReport = useShowEnterpriseCsvReport();
    const [selectedFramework, setSelectedFramework] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [reportDownloaded, setReportDownloaded] = useState(false);
    const [reportError, setReportError] = useState(null);
    const [trustServicesCategories, setTrustServicesCategories] = useState(null);

    // A bar's height is itself a readiness claim, so a framework that has no denominator to be
    // measured against cannot have one — it would assert a precision the figure does not have.
    // Those frameworks are listed below the chart with their evidence counts instead.
    const chartData = useMemo(() => {
        if (!Array.isArray(complianceData) || !complianceData.length) return [];
        return complianceData.filter(d => d.totalObservedControls > 0 && !isUnscorable(d));
    }, [complianceData]);

    const unscorableFrameworks = useMemo(() => {
        if (!Array.isArray(complianceData)) return [];
        return complianceData.filter(d => d.totalObservedControls > 0 && isUnscorable(d));
    }, [complianceData]);

    // Frameworks with no evidence for any currently enforceable requirement but some for a
    // requirement that is approved and not yet enforceable (NERC CIP with CIP-015 evidence only).
    // They have no current figure, so no bar, but their future readiness must still be reachable.
    const futureOnlyFrameworks = useMemo(() => {
        if (!Array.isArray(complianceData)) return [];
        return complianceData.filter(isFutureReadinessOnly);
    }, [complianceData]);

    const handleBarClick = useCallback((entry) => {
        if (entry) {
            setSelectedFramework(entry);
            setReportDownloaded(false);
            setReportError(null);
        }
    }, []);

    // SOC 2 only: the summary endpoint carries no Trust Services Category breakdown, so fetch
    // the report JSON once when the modal opens (SOC 2 skips LLM enrichment). Loading and error
    // states are quiet — the table simply does not render. No request for any other framework.
    const selectedFrameworkName = selectedFramework?.framework;
    useEffect(() => {
        setTrustServicesCategories(null);
        if (!isSoc2Framework(selectedFrameworkName) || !assessmentId) return undefined;
        let cancelled = false;
        apiCall(
            'GET',
            `/enterpriseAssessments/${assessmentId}/grc-compliance-report?framework=${encodeURIComponent(selectedFrameworkName)}`
        )
            .then(data => {
                if (!cancelled && Array.isArray(data?.trustServicesCategories)) {
                    setTrustServicesCategories(data.trustServicesCategories);
                }
            })
            .catch(() => { /* quiet: no table, no banner */ });
        return () => { cancelled = true; };
        // apiCall is intentionally omitted: fetch once per modal open, not per render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFrameworkName, assessmentId]);

    const handleCloseModal = useCallback(() => {
        setSelectedFramework(null);
        setReportDownloaded(false);
        setReportError(null);
    }, []);

    const handleGenerateReport = useCallback(async () => {
        if (!selectedFramework || !assessmentId) return;
        setReportLoading(true);
        setReportError(null);
        setReportDownloaded(false);
        try {
            const data = await apiCall(
                'GET',
                `/enterpriseAssessments/${assessmentId}/grc-compliance-report?framework=${encodeURIComponent(selectedFramework.framework)}`
            );

            // Build CSV from the report's control rows (catalog frameworks list every requirement,
            // including Not Assessed). The underlying questions and answers are deliberately not
            // exported: the report states each control's status, not the assessment behind it.
            const controls = data.controls || [];
            const hasDod = !!data.dodAssessment;
            const hasTier = !!data.csfTierAssessment;
            const csvRows = [
                `Category,Control ID,Control Description,Status,Evidence Credit${hasDod ? ',DoD Points' : ''}`
            ];
            // Rows outside the score (NERC CIP's phased CIP-015 parts) follow the scored rows under
            // their own heading row, as the PDF prints them in their own section (AX-213). SOC 2's
            // unscored criteria stay in catalog order: their category is already a section.
            const separateUnscored = !isSoc2Framework(selectedFramework.framework);
            const scoredControls = separateUnscored ? controls.filter(c => c.isScored !== false) : controls;
            const unscoredControls = separateUnscored ? controls.filter(c => c.isScored === false) : [];
            let unscoredHeading = null;
            for (const c of [...scoredControls, ...unscoredControls]) {
                if (c.isScored === false && separateUnscored && c.category !== unscoredHeading) {
                    unscoredHeading = c.category;
                    csvRows.push('');
                    csvRows.push(escapeCsvField(c.category));
                }
                const cells = [
                    escapeCsvField(c.category),
                    escapeCsvField(c.controlId),
                    escapeCsvField(c.description),
                    escapeCsvField(c.status),
                    escapeCsvField(controlCreditCell(c)),
                ];
                if (hasDod) cells.push(escapeCsvField(c.dodPoints === 0 ? 'NA' : c.dodPoints));
                csvRows.push(cells.join(','));
            }
            // ISO 27001:2022 only: the management-system clauses 4–10 that carry evidence. Listed
            // outside the Annex A readiness indicator and never counted; the questions and answers
            // behind them are deliberately not exported (AX-200).
            if (data.managementSystemClauses) {
                const { title } = data.managementSystemClauses;
                const clauses = clauseReferencesFrom(data.managementSystemClauses);
                csvRows.push('');
                csvRows.push(escapeCsvField(title));
                csvRows.push('Clause');
                if (clauses.length === 0) {
                    csvRows.push('None');
                } else {
                    for (const clause of clauses) csvRows.push(escapeCsvField(clause));
                }
            }
            // SOC 2 only: one row per Trust Services Category — counts, scope wording and the
            // readiness of every scored category (the Common Criteria plus any optional category
            // declared in scope). Readiness is the last cell; the PDF table orders it differently.
            if (data.trustServicesCategories) {
                csvRows.push('');
                csvRows.push(...buildTrustServicesCategoryCsvRows(data.trustServicesCategories, escapeCsvField));
            }
            csvRows.push('');
            csvRows.push(`${escapeCsvField(READINESS_LABEL)},${escapeCsvField(formatReadiness(data))}`);
            // The PDF's Passing / Partial / Failing tiles, in the same words (AX-213). Counts of the
            // scored items only: phased or out-of-scope rows are in none of them.
            const countNoun = itemNounFor({ ...selectedFramework, ...data });
            csvRows.push(`Passing ${countNoun},${data.uniquePassingControls ?? 0}`);
            csvRows.push(`Partial ${countNoun},${data.uniquePartialControls ?? 0}`);
            csvRows.push(`Failing ${countNoun},${data.uniqueFailingControls ?? 0}`);
            if (hasDod) {
                csvRows.push(`Estimated score (DoD methodology crosswalk),${data.dodAssessment.score} / ${data.dodAssessment.maxScore}`);
                csvRows.push(`Requirements Met,${data.dodAssessment.requirementsMet}`);
                csvRows.push(`Requirements Not Met,${data.dodAssessment.requirementsNotMet}`);
                csvRows.push(`Requirements Requiring Action,${data.dodAssessment.requirementsRequiringAction}`);
                csvRows.push(`SSP Established (3.12.4 met),${data.dodAssessment.sspEstablished ? 'Yes' : 'No'}`);
            }
            if (hasTier) {
                const t = data.csfTierAssessment;
                csvRows.push(`${escapeCsvField(t.label ?? 'Axaris-assessed NIST CSF 2.0 Implementation Tier')},${escapeCsvField(tierHeadlineText(t))}`);
                if (t.calculationNote) csvRows.push(`Tier Calculation,${escapeCsvField(t.calculationNote)}`);
                csvRows.push(`${escapeCsvField(t.governanceComponentName ?? 'Current Governance Tier')},${escapeCsvField(componentCellText(t, 'governance'))}`);
                csvRows.push(`${escapeCsvField(t.managementComponentName ?? 'Current Management Tier')},${escapeCsvField(componentCellText(t, 'management'))}`);
                csvRows.push(`Next Tier to Consider,${escapeCsvField(suggestedNextText(t))}${nextTierNoteText(t) ? `,${escapeCsvField(nextTierNoteText(t))}` : ''}`);
                for (const row of tierCoverageRows(t)) {
                    csvRows.push(`Tier Coverage,${escapeCsvField(row.label)},${escapeCsvField(tierCoverageCaption(row))}`);
                }
                csvRows.push(`Tier Evidence Confidence,${escapeCsvField(confidenceText(t))}`);
                csvRows.push(`Tier Response Coverage,${escapeCsvField(coverageText(t))}`);
                if (completenessText(t)) csvRows.push(`Tier Characteristic Completeness,${escapeCsvField(completenessText(t))}`);
                if (determinedNoteText(t)) csvRows.push(`Tier Determination Note,${escapeCsvField(determinedNoteText(t))}`);
                csvRows.push(`Tier Profile,${escapeCsvField(t.profileLabel ?? '')}`);
                for (const c of (t.characteristics || [])) {
                    csvRows.push([
                        'Tier Characteristic',
                        escapeCsvField(c.name),
                        escapeCsvField(c.axis),
                        escapeCsvField(characteristicSummaryText(c)),
                        c.assessed ? 'evidence sufficient' : 'evidence insufficient',
                        c.setsOverallTier ? 'sets overall tier' : '',
                    ].join(','));
                }
                // Tier 2 / 3 / 4 status per characteristic, then (report payload only) the CSF 2.0
                // subcategories behind each tier. Server labels verbatim; nothing derived here.
                csvRows.push(...buildCsfTierStatusCsvRows(t, escapeCsvField));
                csvRows.push(...buildCsfTierMappingCsvRows(t, escapeCsvField));
                csvRows.push(`Tier Disclaimer,${escapeCsvField(t.disclaimer ?? '')}`);
            }
            if (data.methodology) csvRows.push(`Methodology,${escapeCsvField(data.methodology)}`);
            const csvContent = csvRows.join('\n');

            // Trigger download. The leading BOM is what makes Excel read the file as UTF-8:
            // without it Excel falls back to the system codepage and the em dashes in the
            // methodology and status text arrive as "â€"". The charset in the MIME type does not
            // reach Excel — it only describes the download — so the marker has to be in the bytes.
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${selectedFramework.framework.replace(/[^a-zA-Z0-9]/g, '-')}-readiness-report.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setReportDownloaded(true);
        } catch (err) {
            setReportError(err?.response?.data?.message || 'Failed to generate report');
        } finally {
            setReportLoading(false);
        }
    }, [selectedFramework, assessmentId, apiCall]);

    const handleGeneratePdfReport = useCallback(async () => {
        if (!selectedFramework || !assessmentId) return;
        setReportLoading(true);
        setReportError(null);
        setReportDownloaded(false);
        try {
            const response = await apiCall(
                'GET',
                `/enterpriseAssessments/${assessmentId}/grc-compliance-report-pdf?framework=${encodeURIComponent(selectedFramework.framework)}`
            );

            const link = document.createElement('a');
            link.href = `data:application/pdf;base64,${response.pdf_data}`;
            link.download = response.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setReportDownloaded(true);
        } catch (err) {
            setReportError(err?.response?.data?.message || 'Failed to generate PDF report');
        } finally {
            setReportLoading(false);
        }
    }, [selectedFramework, assessmentId, apiCall]);

    // Both collections, not just the chart: an assessment whose only evidence is for frameworks
    // that publish no control count has an empty chart but a non-empty list, and returning null
    // there would hide the evidence-count fallback exactly when it is the only thing to show.
    if (!chartData.length && !unscorableFrameworks.length && !futureOnlyFrameworks.length) return null;

    const dod = selectedFramework?.dodAssessment;
    const tier = selectedFramework?.csfTierAssessment;
    const tierCharacteristics = Array.isArray(tier?.characteristics) ? tier.characteristics : [];
    const tierMeetsAbove = tierCharacteristics.filter(c => c.meetsAbove);
    const tierGaps = tierCharacteristics.filter(c => c.gap);
    // No current figure at all: the modal states that instead of a percentage and its breakdown.
    const futureOnly = !!selectedFramework && isFutureReadinessOnly(selectedFramework);
    const showBenchmark = !!selectedFramework && selectedFramework.industryAverageCoverage != null && !isUnscorable(selectedFramework) && !futureOnly;
    const notAssessed = selectedFramework?.notAssessedControls ?? Math.max(0, (selectedFramework?.canonicalControlCount ?? 0) - (selectedFramework?.totalObservedControls ?? 0));
    // Absent (every framework but NERC CIP) or empty renders nothing at all.
    const futureEffectiveControls = futureEffectiveControlsOf(selectedFramework);
    // Null unless the server reports a scoped denominator below the catalog count (NERC CIP).
    const applicabilityLine = selectedFramework ? buildApplicabilityLine(selectedFramework) : null;

    return (
        <Card>
            <h3 className="text-lg font-semibold mb-1">GRC Readiness Estimate</h3>
            <p className="text-sm text-gray-500 mb-4">
                {READINESS_LABEL} per framework, from question evidence, remediations and findings. Click a bar for details and reports.
            </p>
            {chartData.length > 0 && (
            <>
            <div style={{ width: "100%", height: 250 }}>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 5, right: 25, left: 5, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="framework"
                            type="category"
                            tick={{ fontSize: 12 }}
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                            height={120}
                        />
                        <YAxis type="number" domain={[0, 100]} unit="%" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="adjustedCoveragePercent" name="adjustedCoveragePercent" barSize={30} minPointSize={5} onClick={handleBarClick}>
                            {chartData.map((entry) => (
                                <Cell key={entry.framework} fill={getBarColor(entry.adjustedCoveragePercent)} cursor="pointer" />
                            ))}
                        </Bar>
                        <Bar dataKey="industryAverageCoverage" name="industryAverageCoverage" barSize={30} fill={INDUSTRY_AVG_COLOR} opacity={0.7} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: "#166534" }} /> &ge;90%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block bg-green-500" /> 70–89%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block bg-yellow-400" /> 50–69%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block bg-orange-500" /> 25–49%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block bg-red-600" /> &lt;25%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: INDUSTRY_AVG_COLOR, opacity: 0.7 }} /> {BENCHMARK_LABEL}</span>
            </div>
            <p className="mt-2 text-xs text-gray-400">
                The peer benchmark is an Axaris analyst estimate for the organisation's industry, shown for comparison only — it is not part of the Readiness Indicator.
            </p>
            </>
            )}

            {/* Frameworks with evidence but no published requirement count. They are shown rather
                than hidden — the evidence is real and the customer should see it — but as counts,
                because there is no denominator to turn them into a percentage. Each entry opens
                the same details modal a bar does: no bar must not mean no reports (AX-185). */}
            {unscorableFrameworks.length > 0 && (
                <div className="mt-3 border-t border-gray-100 pt-2">
                    <p className="text-xs text-gray-500">
                        These frameworks publish no official control count, so no readiness percentage is calculated for them. Click one for details and reports:
                    </p>
                    <ul className="mt-1 text-xs text-gray-500 space-y-0.5">
                        {unscorableFrameworks.map(f => (
                            <li key={f.framework}>
                                <button
                                    type="button"
                                    onClick={() => handleBarClick(f)}
                                    className="text-left hover:underline focus:outline-none focus:underline"
                                >
                                    <span className="font-medium text-blue-700">{f.frameworkDisplayName || f.framework}</span>
                                    {' — '}{f.uniquePassingControls} passing, {f.uniquePartialControls} partial, {f.uniqueFailingControls} failing
                                    {' '}of {f.totalObservedControls} controls with evidence
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {futureOnlyFrameworks.length > 0 && (
                <div className="mt-3 border-t border-gray-100 pt-2">
                    <p className="text-xs text-gray-500">
                        These frameworks have evidence only for requirements that are approved but not yet enforceable, so no current readiness percentage is calculated for them. Click one for future readiness details and reports:
                    </p>
                    <ul className="mt-1 text-xs text-gray-500 space-y-0.5">
                        {futureOnlyFrameworks.map(f => {
                            const future = futureEffectiveControlsOf(f);
                            const withEvidence = future.filter(c => futureStatusLabel(c.status) !== 'Not Assessed').length;
                            return (
                                <li key={f.framework}>
                                    <button
                                        type="button"
                                        onClick={() => handleBarClick(f)}
                                        className="text-left hover:underline focus:outline-none focus:underline"
                                    >
                                        <span className="font-medium text-blue-700">{f.frameworkDisplayName || f.framework}</span>
                                        {' — '}future readiness only: {withEvidence} of {future.length} not-yet-enforceable requirements have evidence
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <Modal
                isOpen={!!selectedFramework}
                onClose={handleCloseModal}
                title={selectedFramework?.frameworkDisplayName || selectedFramework?.framework || 'Framework Details'}
                size="lg"
            >
                {selectedFramework && (
                    <div className="space-y-4">
                        {/* The peer benchmark is a comparison against the indicator, so it is
                            omitted when the framework has no indicator to compare against. */}
                        <div className={`grid gap-4 text-sm ${showBenchmark ? 'grid-cols-2' : 'grid-cols-1'}`}>
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-gray-500">{READINESS_LABEL}</div>
                                <div className="text-xl font-bold">{futureOnly ? FUTURE_ONLY_READINESS_TEXT : formatReadiness(selectedFramework)}</div>
                            </div>
                            {showBenchmark && (
                                <div className="bg-slate-50 p-3 rounded">
                                    <div className="text-gray-500">{BENCHMARK_LABEL}</div>
                                    <div className="text-xl font-bold text-slate-500">{selectedFramework.industryAverageCoverage}%</div>
                                </div>
                            )}
                        </div>
                        {futureOnly ? (
                            <div className="text-xs text-gray-500">{FUTURE_ONLY_EXPLANATION}</div>
                        ) : (
                        <>
                        <div className={`grid gap-4 text-sm ${selectedFramework.uniquePartialControls > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-gray-500">Passing {itemNounFor(selectedFramework)}</div>
                                <div className="text-xl font-bold text-green-600">{selectedFramework.uniquePassingControls}</div>
                            </div>
                            {selectedFramework.uniquePartialControls > 0 && (
                                <div className="bg-amber-50 p-3 rounded">
                                    <div className="text-gray-500">Partial {itemNounFor(selectedFramework)}</div>
                                    <div className="text-xl font-bold text-amber-600">{selectedFramework.uniquePartialControls}</div>
                                </div>
                            )}
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-gray-500">Failing {itemNounFor(selectedFramework)}</div>
                                <div className="text-xl font-bold text-red-600">{selectedFramework.uniqueFailingControls}</div>
                            </div>
                        </div>
                        <div className="text-xs text-gray-500">
                            {buildEvidenceLine({ ...selectedFramework, notAssessed, noun: itemNounPluralOf(selectedFramework) })}
                        </div>
                        </>
                        )}
                        {applicabilityLine && (
                            <div className="text-xs text-gray-500">{applicabilityLine}</div>
                        )}

                        {futureEffectiveControls.length > 0 && (
                            <FutureReadinessBlock controls={futureEffectiveControls} />
                        )}

                        {isSoc2Framework(selectedFramework.framework) && Array.isArray(trustServicesCategories) && trustServicesCategories.length > 0 && (
                            <div className="text-sm">
                                <div className="font-semibold mb-1">Trust Services Categories</div>
                                <table className="w-full text-xs border border-gray-200">
                                    <thead className="bg-gray-50 text-gray-500">
                                        <tr>
                                            <th className="text-left px-2 py-1">Category</th>
                                            <th className="text-left px-2 py-1">Evidenced / Total</th>
                                            <th className="text-left px-2 py-1">Scope</th>
                                            <th className="text-left px-2 py-1">Readiness</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trustServicesCategories.map(c => (
                                            <tr key={c.category} className="border-t border-gray-200">
                                                <td className="px-2 py-1">{c.category}{c.scored ? '' : <span className="text-gray-400"> (reported, not scored)</span>}</td>
                                                {isExcludedCategory(c) ? (
                                                    <td className="px-2 py-1 text-gray-500" colSpan={3}>{scopeWording(c)}</td>
                                                ) : (
                                                    <>
                                                        <td className="px-2 py-1">
                                                            {c.assessed} / {c.total}
                                                            {c.scored && <span className="text-gray-500"> · {passingWording(c)}</span>}
                                                        </td>
                                                        <td className="px-2 py-1 text-gray-500">{scopeWording(c)}</td>
                                                        <td className="px-2 py-1">{readinessWording(c)}</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {scopeHintLine(trustServicesCategories) && (
                                    <p className="mt-1 text-xs text-gray-500">{scopeHintLine(trustServicesCategories)}</p>
                                )}
                            </div>
                        )}

                        {dod && (
                            <div className="border border-blue-100 bg-blue-50 rounded p-3 text-sm space-y-2">
                                <div className="flex items-baseline justify-between">
                                    <div className="font-semibold text-blue-900">Estimated score — DoD methodology crosswalk</div>
                                    <div className="text-xs text-blue-700">{dod.methodology}</div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div>
                                        <div className="text-gray-500 text-xs">Estimated Score</div>
                                        <div className="text-2xl font-bold text-blue-900">{dod.score} <span className="text-sm font-normal text-gray-500">/ {dod.maxScore}</span></div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 text-xs">Requirements Met</div>
                                        <div className="text-xl font-bold text-green-700">{dod.requirementsMet}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 text-xs">Requirements Not Met</div>
                                        <div className="text-xl font-bold text-red-700">{dod.requirementsNotMet}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 text-xs">Requirements Requiring Action</div>
                                        <div className="text-xl font-bold text-amber-700">{dod.requirementsRequiringAction}</div>
                                    </div>
                                </div>
                                <div className="text-xs text-gray-600">
                                    Evidence coverage: <span className="font-semibold">{dod.evidenceCoveragePercent}% ({dod.evidenceCoverageBand})</span>
                                    {' · '}{dod.assessedRequirements} of {dod.totalRequirements} requirements have evidence
                                    {dod.notAssessed > 0 && <>, {dod.notAssessed} not assessed (deducted in full)</>}
                                    . Partial earns no credit.
                                </div>
                                {!dod.sspEstablished && (
                                    <div className="text-xs text-red-700">
                                        No system security plan established (3.12.4 not met). A DoD Basic Assessment is a review of the SSP, so this figure is a crosswalk estimate, not an assessment score.
                                    </div>
                                )}
                                <div className="text-xs text-gray-400">
                                    Axaris-computed against the 110 Rev. 2 requirements; not a DoD, DIBCAC or C3PAO result. Not included in the client PDF.
                                </div>
                            </div>
                        )}

                        {tier && (
                            <div className="border border-blue-100 bg-blue-50 rounded p-3 text-sm space-y-2">
                                <div className="flex items-baseline justify-between gap-2">
                                    <div className="font-semibold text-blue-900">{tier.label ?? 'Axaris-assessed NIST CSF 2.0 Implementation Tier'}</div>
                                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Estimate</span>
                                </div>
                                <div className="text-xs text-gray-500">{tier.profileLabel}</div>
                                <CsfTierBar tier={tier} />
                                {/* Two columns always: the modal is max-w-lg, so a viewport md: breakpoint would
                                    squeeze four ~100px cells and "3 — Repeatable" overran its neighbour (AX-173). */}
                                <div className="grid grid-cols-2 gap-3 break-words">
                                    <div>
                                        <div className="text-gray-500 text-xs">Current Overall Tier</div>
                                        <div className={tier.overallTierLabel != null ? 'text-lg font-bold text-blue-900' : 'text-sm font-semibold text-gray-600'}>
                                            {tierHeadlineText(tier)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 text-xs">{tier.governanceComponentName ?? 'Current Governance Tier'}</div>
                                        <div className={tier.governanceTierLabel != null ? 'text-base font-bold text-blue-900' : 'text-sm font-semibold text-gray-600'}>{componentCellText(tier, 'governance')}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 text-xs">{tier.managementComponentName ?? 'Current Management Tier'}</div>
                                        <div className={tier.managementTierLabel != null ? 'text-base font-bold text-blue-900' : 'text-sm font-semibold text-gray-600'}>{componentCellText(tier, 'management')}</div>
                                    </div>
                                    <div className="text-gray-600">
                                        <div className="text-gray-500 text-xs">Next Tier to Consider</div>
                                        <div className={(tier.nextTierLabel ?? tier.suggestedNextTierLabel) != null ? 'text-base font-bold text-gray-700' : 'text-sm'}>{suggestedNextText(tier)}</div>
                                        {nextTierNoteText(tier) && (
                                            <div className="text-xs text-gray-500">{nextTierNoteText(tier)}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="text-xs text-gray-600">{confidenceText(tier)}</div>
                                <div className="text-xs text-gray-600">{coverageText(tier)}</div>
                                {completenessText(tier) && (
                                    <div className="text-xs text-gray-600">{completenessText(tier)}</div>
                                )}
                                {determinedNoteText(tier) && (
                                    <div className="text-xs text-gray-700 bg-white/60 border border-blue-100 rounded px-2 py-1">
                                        {determinedNoteText(tier)}
                                    </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <div className="text-xs font-semibold text-green-800 mb-1">Meets criteria above current tier</div>
                                        {tierMeetsAbove.length === 0 ? (
                                            <div className="text-xs text-gray-500">None</div>
                                        ) : (
                                            <ul className="text-xs text-gray-700 space-y-0.5">
                                                {tierMeetsAbove.map(c => (
                                                    <li key={c.key ?? c.name}>{c.name} — {characteristicSummaryText(c)}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-xs font-semibold text-red-800 mb-1">{gapsHeadingText(tier)}</div>
                                        {tierGaps.length === 0 ? (
                                            <div className="text-xs text-gray-500">None</div>
                                        ) : (
                                            <div className="space-y-1">
                                                {tierGaps.map(c => (
                                                    <details key={c.key ?? c.name} className="text-xs text-gray-700">
                                                        <summary className="cursor-pointer">
                                                            {gapItemText(c)}
                                                        </summary>
                                                        <TierGapImprovements characteristic={c} />
                                                    </details>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="text-xs text-gray-400">{tier.disclaimer}</div>
                            </div>
                        )}

                        {reportLoading ? (
                            <div className="flex flex-col items-center justify-center py-4 space-y-3">
                                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                                <p className="text-sm text-gray-600 text-center">
                                    Generating report... This may take a minute. Please do not leave this page.
                                </p>
                            </div>
                        ) : (
                            <div className="flex justify-end gap-2">
                                {showCsvReport && (
                                    <Button
                                        onClick={handleGenerateReport}
                                        disabled={!assessmentId}
                                    >
                                        Download CSV Report
                                    </Button>
                                )}
                                <Button
                                    onClick={handleGeneratePdfReport}
                                    disabled={!assessmentId}
                                >
                                    Download PDF Report
                                </Button>
                            </div>
                        )}

                        {reportError && (
                            <div className="text-red-600 text-sm bg-red-50 p-3 rounded">{reportError}</div>
                        )}

                        {reportDownloaded && (
                            <div className="text-green-700 text-sm bg-green-50 p-3 rounded">
                                Report downloaded successfully.
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </Card>
    );
};

export default GrcComplianceEstimate;
